/**
 * CompactUpdateSections — the compact popover body for an UPDATE statement.
 * Reuses the full-view primitives (`SetAssignmentsList`, `FilterPills`) over
 * the UPDATE clause outline, so the popover shows the statement's REAL
 * SET / WHERE instead of SELECT-shaped sections.
 *
 * DML-14/16/21 — the banner (`DmlTargetCard variant="banner"`) resolves the
 * bare `UPDATE p` alias against the FROM/multi-table tail using the SAME
 * helper trio + call pattern `UpdateRenderer` uses for its full-view target
 * pill (#10), and a read-only From/Joins row + RETURNING row give visible
 * context for the FROM/JOIN/RETURNING clauses that only SET/WHERE are
 * editable here — mirroring, not duplicating, `UpdateRenderer`'s span slicing.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import type { UpdateOutline } from '../../render/update/extractUpdateOutline';
import { DmlTargetCard } from '../../render/common/DmlTargetCard';
import { dmlWhereScope } from '../../render/common/dmlWhereScope';
import { ClauseCard } from '../../render/select/ClauseCard';
import { extractFromSegmentJoins } from '../../render/select/extractFromJoins';
import { FilterPills } from '../../render/select/FilterPills';
import { KIND_LABELS } from '../../render/select/joinKindMaps';
import { splitTopLevelCommas } from '../../render/select/splitTopLevel';
import {
  buildAliasCandidatesFromText,
  formatDmlTargetText,
  readBareAliasTail,
  resolveDmlTargetAlias,
} from '../../render/update/resolveDmlTargetAlias';
import { SetAssignmentsList } from '../../render/update/SetAssignmentsList';

export interface CompactUpdateSectionsProps {
  readonly bufferText: string;
  readonly outline: UpdateOutline;
  readonly options: ParseOptions;
  readonly editContext: VqeEditContext | null;
  /**
   * Show the `UPDATE <target>` banner row. The popover header already
   * names the target (with the same write accent) whenever it resolves one,
   * so the host turns this off then — the row only fills in when the header
   * could say no more than the verb. Defaults to on.
   */
  readonly showTargetBanner?: boolean;
}

// TODO(vqe-copy): consolidate into vqeCopy (lane #4 owns vqeCopy.ts this batch).
const RETURNING_CONTEXT_SUFFIX = 'edited in the full editor';

interface FromJoinSummary {
  readonly leadingText: string | null;
  readonly joinsText: string | null;
}

/** Flattens a comma-separated FROM/multi-table span into "From" + "Joins" text summaries. */
function summarizeFromJoins(
  bufferText: string,
  start: number,
  end: number,
  options: ParseOptions,
): FromJoinSummary {
  const leadingParts: string[] = [];
  const joinParts: string[] = [];
  for (const seg of splitTopLevelCommas(bufferText, start, end, options)) {
    const split = extractFromSegmentJoins(bufferText, seg.start, seg.end, options);
    const leadingText = split.leading.text || seg.text;
    if (leadingText) leadingParts.push(leadingText);
    for (const join of split.joins) {
      const label = KIND_LABELS[join.kind];
      joinParts.push(join.rhs.text ? `${label} ${join.rhs.text}` : label);
    }
  }
  return {
    leadingText: leadingParts.length > 0 ? leadingParts.join(', ') : null,
    joinsText: joinParts.length > 0 ? joinParts.join(', ') : null,
  };
}

export function CompactUpdateSections({
  bufferText,
  outline,
  options,
  editContext,
  showTargetBanner = true,
}: CompactUpdateSectionsProps) {
  const ctx = editContext ?? undefined;
  const whereSpan =
    outline.whereStart !== null && outline.whereEnd !== null
      ? { start: outline.whereStart, end: outline.whereEnd }
      : null;

  // Same no-WHERE detector as the Visual view: with no WHERE the WHERE row
  // turns red and says which rows change; the popover header carries the
  // red "No WHERE" badge.
  const whereScope = dmlWhereScope(outline);

  // DML-14 — resolve the banner alias against the FROM/multi-table tail,
  // mirroring UpdateRenderer's target-pill resolution exactly (#10).
  let bannerTargetText = outline.targetText ?? '';
  let fromRowText: string | null = null;
  let joinsRowText: string | null = null;

  if (outline.targetText !== null) {
    if (outline.fromStart !== null && outline.fromEnd !== null) {
      const candidates = buildAliasCandidatesFromText(
        bufferText,
        outline.fromStart,
        outline.fromEnd,
        options,
      );
      const resolved = resolveDmlTargetAlias(outline.targetText, candidates);
      bannerTargetText = formatDmlTargetText(outline.targetText, resolved);
      const summary = summarizeFromJoins(bufferText, outline.fromStart, outline.fromEnd, options);
      fromRowText = summary.leadingText;
      joinsRowText = summary.joinsText;
    } else if (outline.multiTableStart !== null && outline.multiTableEnd !== null) {
      const tail = readBareAliasTail(
        bufferText,
        outline.multiTableStart,
        outline.multiTableEnd,
        options,
      );
      if (tail.candidate && !tail.hasJoins) {
        // Oracle DML-19 shape — a lone alias with no real JOIN adds no
        // information as a separate row; merge into the banner instead
        // (parity with UpdateRenderer's showTablesCard=false).
        const resolved = resolveDmlTargetAlias(outline.targetText, [tail.candidate]);
        bannerTargetText = formatDmlTargetText(outline.targetText, resolved);
      } else {
        // MySQL JOIN/comma tail — rebase to the target start when the tail's
        // leading token is just the target's own alias leaking through
        // (`customer c INNER JOIN …`) so the row reads "customer c", not a
        // bare, information-free "c" — mirrors UpdateRenderer's Tables span.
        const rebaseStart =
          tail.candidate && outline.targetStart !== null
            ? outline.targetStart
            : outline.multiTableStart;
        const summary = summarizeFromJoins(bufferText, rebaseStart, outline.multiTableEnd, options);
        fromRowText = summary.leadingText;
        joinsRowText = summary.joinsText;
      }
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="vqe-compact-update-sections">
      {showTargetBanner ? (
        <DmlTargetCard intent="update" variant="banner" targetText={bannerTargetText} />
      ) : null}

      {fromRowText || joinsRowText ? (
        <div
          className="flex flex-col gap-0.5 rounded-md border border-border/60 bg-muted/20 px-2 py-1 text-[11px] text-muted-foreground"
          data-testid="vqe-compact-from-row"
        >
          {fromRowText ? (
            <span data-testid="vqe-compact-from-row-from">
              From: <span className="font-mono text-foreground/80">{fromRowText}</span>
            </span>
          ) : null}
          {joinsRowText ? (
            <span data-testid="vqe-compact-from-row-joins">
              Joins: <span className="font-mono text-foreground/80">{joinsRowText}</span>
            </span>
          ) : null}
        </div>
      ) : null}

      {outline.setStart !== null && outline.setEnd !== null ? (
        <ClauseCard title="Set" testId="vqe-compact-update-set">
          <SetAssignmentsList
            bufferText={bufferText}
            start={outline.setStart}
            end={outline.setEnd}
            options={options}
            testIdPrefix="vqe-compact-update-set"
            editContext={ctx}
          />
        </ClauseCard>
      ) : null}

      <FilterPills
        bufferText={bufferText}
        span={whereSpan}
        options={options}
        kind="where"
        editContext={ctx}
        density="compact"
        destructiveGuard={{ verb: 'UPDATE', targetText: bannerTargetText, scope: whereScope }}
      />

      {outline.returningStart !== null && outline.returningEnd !== null ? (
        <div
          className="rounded-md border border-border/60 bg-muted/20 px-2 py-1 text-[11px] text-muted-foreground"
          data-testid="vqe-compact-returning-row"
        >
          <span className="font-mono text-foreground/80">
            RETURNING{' '}
            {splitTopLevelCommas(bufferText, outline.returningStart, outline.returningEnd, options)
              .map((item) => item.text)
              .join(', ')}
          </span>{' '}
          — {RETURNING_CONTEXT_SUFFIX}
        </div>
      ) : null}
    </div>
  );
}
